import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Tables, TablesInsert } from '@/integrations/supabase/types';

export type RentalVehicle = Tables<'rental_vehicles'>;
export type RentalVehicleInsert = Omit<TablesInsert<'rental_vehicles'>, 'id' | 'created_at' | 'updated_at'>;

export interface RentalVehicleFilters {
  category?: string;
  minPrice?: number;
  maxPrice?: number;
  passengers?: number;
  transmission?: string;
  features?: string[];
}

export const useRentalVehicles = (filters?: RentalVehicleFilters) => {
  return useQuery({
    queryKey: ['rental-vehicles', filters],
    queryFn: async () => {
      let query = supabase
        .from('rental_vehicles')
        .select('*')
        .eq('is_active', true)
        .eq('is_available', true);

      if (filters?.category) {
        query = query.eq('category_id', filters.category);
      }

      if (filters?.minPrice) {
        query = query.gte('daily_rate', filters.minPrice);
      }

      if (filters?.maxPrice) {
        query = query.lte('daily_rate', filters.maxPrice);
      }

      if (filters?.passengers) {
        query = query.gte('passengers', filters.passengers);
      }

      if (filters?.transmission) {
        query = query.eq('transmission', filters.transmission);
      }

      query = query.order('is_featured', { ascending: false }).order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;
      return data as RentalVehicle[];
    },
  });
};

export const useRentalVehicle = (slug: string) => {
  return useQuery({
    queryKey: ['rental-vehicle', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rental_vehicles')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (error) throw error;
      return data as RentalVehicle;
    },
    enabled: !!slug,
  });
};

export const useAdminRentalVehicles = () => {
  return useQuery({
    queryKey: ['admin-rental-vehicles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rental_vehicles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as RentalVehicle[];
    },
  });
};

export const useCreateRentalVehicle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vehicle: RentalVehicleInsert) => {
      const { data, error } = await supabase
        .from('rental_vehicles')
        .insert([vehicle])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['admin-rental-vehicles'] });
      toast.success('Vehicle created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create vehicle');
      console.error('Error creating vehicle:', error);
    },
  });
};

export const useUpdateRentalVehicle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<RentalVehicle> }) => {
      const { data, error } = await supabase
        .from('rental_vehicles')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['admin-rental-vehicles'] });
      toast.success('Vehicle updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update vehicle');
      console.error('Error updating vehicle:', error);
    },
  });
};

export const useDeleteRentalVehicle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('rental_vehicles')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['admin-rental-vehicles'] });
      toast.success('Vehicle deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete vehicle');
      console.error('Error deleting vehicle:', error);
    },
  });
};